import React, { useEffect, useState } from "react";
import { DashboardLayout } from "../../../Layouts/Dashboard/Index";
import CustomBreadcrumb from "../../../Components/custom-breadcrumb/CustmBreadcrumb";
import AttendanceDataTable from "../../../Components/Table/attendance/AttendanceDataTable";
import { getParticipantsAttendance } from "../../../hooks/useAddParticipants";
import {
  ParticipantsList,
  RoomAttendanceCount,
  RoomDetailsType,
} from "../../../types/common";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import moment from "moment";
import LoadingState from "../../../Components/Loading";
import {
  getRoomAttendanceCountDetails,
  getRoomAttendanceDetailsByDateAndRoomNo,
} from "../../../hooks/useAddRoomParticipants";

const RoomAttendancePage = () => {
  const [loading, setLoading] = useState(false);
  const [participants, setParticipants] = useState<
    ParticipantsList[] | undefined
  >([]);
  const [attendanceCounts, setAttendanceCounts] =
    useState<RoomAttendanceCount | null>(null);
  const [exportLoading, setExportLoading] = useState<{
    [key: string]: boolean;
  }>({});

  useEffect(() => {
    setLoading(true);

    // Fetch room attendance counts
    getRoomAttendanceCountDetails()
      .then((roomCounts) => {
        setAttendanceCounts(roomCounts);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching room attendance counts:", error);
        setLoading(false);
      });
  }, []);

  // Format date and time for Excel export
  const formatDateTime = (date: any) => {
    if (!date) return "Not Recorded";

    try {
      // Handle Firebase timestamp (with seconds property)
      if (typeof date === "object" && date.seconds) {
        return moment(new Date(date.seconds * 1000)).format(
          "YYYY/MM/DD hh:mm A"
        );
      }

      // Handle string timestamp like "Wed Sep 24 2025 02:50:45 GMT+0530 (India Standard Time)"
      if (typeof date === "string") {
        return moment(new Date(date)).format("YYYY/MM/DD hh:mm A");
      }

      // Handle JavaScript Date object
      if (date instanceof Date) {
        return moment(date).format("YYYY/MM/DD hh:mm A");
      }

      // Fallback: try to convert whatever format we have
      return moment(new Date(date)).format("YYYY/MM/DD hh:mm A");
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid Date";
    }
  };

  // Export room attendance data to Excel
  const exportRoomAttendanceToExcel = async (day: number, roomNo: number) => {
    const key = `day${day}room${roomNo}`;
    setExportLoading({ ...exportLoading, [key]: true });

    try {
      // Get attendance details for the specific room and day
      const attendanceDetails = await getRoomAttendanceDetailsByDateAndRoomNo(
        day,
        roomNo
      );

      if (attendanceDetails && attendanceDetails.length > 0) {
        // Format data for Excel
        const excelData = attendanceDetails.map((item) => ({
          RoomNo: item.roomNo || "",
          Day: item.day,
          Name: item.name || "",
          Email: item.email || "",
          Phone: item.phone || "",
          Team: item.district || "",
          Designation: item.dealershipName || "",
          ClockedIn: formatDateTime(item.clockedIn),
        }));

        // Create worksheet
        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

        // Generate Excel file
        const excelBuffer = XLSX.write(workbook, {
          bookType: "xlsx",
          type: "array",
        });

        // Create and download blob
        const blob = new Blob([excelBuffer], {
          type: "application/octet-stream",
        });

        saveAs(blob, `Room${roomNo}_Day${day}_Attendance.xlsx`);
      } else {
        alert(`No attendance data found for Room ${roomNo} on Day ${day}`);
      }
    } catch (error) {
      console.error(
        `Error exporting attendance for Room ${roomNo} on Day ${day}:`,
        error
      );
      alert(`Failed to export data. Please try again.`);
    } finally {
      setExportLoading({ ...exportLoading, [key]: false });
    }
  };

  return (
    <DashboardLayout>
      <div className="pt-10">
        {loading && <LoadingState />}
        <CustomBreadcrumb
          heading="Attendance"
          links={[
            { name: "Dashboard", path: "/dashboard" },
            {
              name: "Room Attendance Count",
              path: "/dashboard/roomAttendance",
            },
          ]}
        />

        <div className="pt-2">
          {/* Room Attendance Cards */}
          <div className="mb-8">
            {/* Day 1 Attendance Details */}
            <div className="mb-6">
              <h2 className="text-xl font-bold mb-4 text-gray-800">
                Day 1 Attendance Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Room 1 Day 1 */}
                <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
                  <h3 className="text-lg font-semibold mb-2">Room 1</h3>
                  <div className="flex items-center mb-4">
                    <div className="bg-blue-100 p-3 rounded-full mr-4">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6 text-blue-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Attendance Count</p>
                      <p className="text-2xl font-bold">
                        {attendanceCounts?.roomOneDayOne || 0}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => exportRoomAttendanceToExcel(1, 1)}
                    disabled={exportLoading["day1room1"]}
                    className="w-full mt-2 px-3 py-2 text-xs font-medium text-center inline-flex items-center justify-center text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:ring-4 focus:outline-none focus:ring-blue-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {exportLoading["day1room1"] ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Exporting...
                      </>
                    ) : (
                      <>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-4 h-4 mr-2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        Export to Excel
                      </>
                    )}
                  </button>
                </div>

                {/* Room 2 Day 1 */}
                <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
                  <h3 className="text-lg font-semibold mb-2">Room 2</h3>
                  <div className="flex items-center mb-4">
                    <div className="bg-green-100 p-3 rounded-full mr-4">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6 text-green-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Attendance Count</p>
                      <p className="text-2xl font-bold">
                        {attendanceCounts?.roomTwoDayOne || 0}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => exportRoomAttendanceToExcel(1, 2)}
                    disabled={exportLoading["day1room2"]}
                    className="w-full mt-2 px-3 py-2 text-xs font-medium text-center inline-flex items-center justify-center text-white bg-green-600 rounded-lg hover:bg-green-700 focus:ring-4 focus:outline-none focus:ring-green-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {exportLoading["day1room2"] ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Exporting...
                      </>
                    ) : (
                      <>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-4 h-4 mr-2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        Export to Excel
                      </>
                    )}
                  </button>
                </div>

                {/* Room 3 Day 1 */}
                <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-purple-500">
                  <h3 className="text-lg font-semibold mb-2">Room 3</h3>
                  <div className="flex items-center mb-4">
                    <div className="bg-purple-100 p-3 rounded-full mr-4">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6 text-purple-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Attendance Count</p>
                      <p className="text-2xl font-bold">
                        {attendanceCounts?.roomThreeDayOne || 0}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => exportRoomAttendanceToExcel(1, 3)}
                    disabled={exportLoading["day1room3"]}
                    className="w-full mt-2 px-3 py-2 text-xs font-medium text-center inline-flex items-center justify-center text-white bg-purple-600 rounded-lg hover:bg-purple-700 focus:ring-4 focus:outline-none focus:ring-purple-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {exportLoading["day1room3"] ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Exporting...
                      </>
                    ) : (
                      <>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-4 h-4 mr-2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        Export to Excel
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Day 2 Attendance Details */}
            <div>
              <h2 className="text-xl font-bold mb-4 text-gray-800">
                Day 2 Attendance Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Room 1 Day 2 */}
                <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-yellow-500">
                  <h3 className="text-lg font-semibold mb-2">Room 1</h3>
                  <div className="flex items-center mb-4">
                    <div className="bg-yellow-100 p-3 rounded-full mr-4">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6 text-yellow-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Attendance Count</p>
                      <p className="text-2xl font-bold">
                        {attendanceCounts?.roomOneDayTwo || 0}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => exportRoomAttendanceToExcel(2, 1)}
                    disabled={exportLoading["day2room1"]}
                    className="w-full mt-2 px-3 py-2 text-xs font-medium text-center inline-flex items-center justify-center text-white bg-yellow-600 rounded-lg hover:bg-yellow-700 focus:ring-4 focus:outline-none focus:ring-yellow-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {exportLoading["day2room1"] ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Exporting...
                      </>
                    ) : (
                      <>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-4 h-4 mr-2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        Export to Excel
                      </>
                    )}
                  </button>
                </div>

                {/* Room 2 Day 2 */}
                <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-red-500">
                  <h3 className="text-lg font-semibold mb-2">Room 2</h3>
                  <div className="flex items-center mb-4">
                    <div className="bg-red-100 p-3 rounded-full mr-4">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6 text-red-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Attendance Count</p>
                      <p className="text-2xl font-bold">
                        {attendanceCounts?.roomTwoDayTwo || 0}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => exportRoomAttendanceToExcel(2, 2)}
                    disabled={exportLoading["day2room2"]}
                    className="w-full mt-2 px-3 py-2 text-xs font-medium text-center inline-flex items-center justify-center text-white bg-red-600 rounded-lg hover:bg-red-700 focus:ring-4 focus:outline-none focus:ring-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {exportLoading["day2room2"] ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Exporting...
                      </>
                    ) : (
                      <>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-4 h-4 mr-2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        Export to Excel
                      </>
                    )}
                  </button>
                </div>

                {/* Room 3 Day 2 */}
                <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-indigo-500">
                  <h3 className="text-lg font-semibold mb-2">Room 3</h3>
                  <div className="flex items-center mb-4">
                    <div className="bg-indigo-100 p-3 rounded-full mr-4">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6 text-indigo-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Attendance Count</p>
                      <p className="text-2xl font-bold">
                        {attendanceCounts?.roomThreeDayTwo || 0}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => exportRoomAttendanceToExcel(2, 3)}
                    disabled={exportLoading["day2room3"]}
                    className="w-full mt-2 px-3 py-2 text-xs font-medium text-center inline-flex items-center justify-center text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:ring-4 focus:outline-none focus:ring-indigo-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {exportLoading["day2room3"] ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Exporting...
                      </>
                    ) : (
                      <>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-4 h-4 mr-2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        Export to Excel
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default RoomAttendancePage;
